import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DIRECTION_LABEL, POLL_CHOICES, intgApi, orcanosFilters, orcanosProjects, partnerOf,
} from '../../utils/integrationsApi';
import { SearchSelect } from '../review/reviewUi';
import OpenFilterLink from '../../components/OpenFilterLink';
import '../../styles/DocReview.css';
import '../../styles/Integrations.css';

/**
 * The integration builder (mockup screen 2) — six steps, like a Zap:
 *   1 Connect · 2 Orcanos side · 3 Jira/GitHub side · 4 Field map · 5 Rules · 6 Test and enable.
 *
 * One builder for both connectors (v4.28.0). `P` is the partner's name ("Jira" / "GitHub"); the
 * form keys stay `jira_*` for both — for GitHub, `jira_project_key` is the repository (owner/name)
 * and `jira_issue_type_id` is "issue" (all issues) or an organisation issue type.
 *
 * Every "Next" saves. Secrets are write-only: the server never sends a stored password or token
 * back, so a blank box means "keep the saved one" (the placeholder says so).
 * Design: design/DESIGN_INTEGRATIONS.md §5–§6.
 */
const stepsFor = (P) => [
  { n: 1, title: 'Connect', sub: `Orcanos and ${P} sign-in` },
  { n: 2, title: 'Orcanos side', sub: 'Filter and how often' },
  { n: 3, title: `${P} side`, sub: P === 'GitHub' ? 'Repository, conditions' : 'Project, type, conditions' },
  { n: 4, title: 'Field map', sub: 'Fields, directions, values' },
  { n: 5, title: 'Rules', sub: 'Creating and conflicts' },
  { n: 6, title: 'Test and enable', sub: 'Dry run on one item' },
];

const COND_OPS = [
  { value: 'is', label: 'is' }, { value: 'is_not', label: 'is not' },
  { value: 'contains', label: 'contains' }, { value: 'not_contains', label: 'does not contain' },
  { value: 'empty', label: 'is empty' }, { value: 'not_empty', label: 'is not empty' },
];

// Drawn in a 2-column grid that fills row by row: this order puts the Orcanos fields in the LEFT
// column and the Jira fields in the RIGHT, the same sides as the field map table above.
const WRITEBACKS = [
  { key: 'jira_key', side: 'orc', label: 'Jira issue key', example: 'PUMP-1042' },
  { key: 'orcanos_id', side: 'jira', label: 'Orcanos item ID', example: '18233' },
  { key: 'jira_url', side: 'orc', label: 'Jira issue link', example: '…/browse/PUMP-1042' },
  { key: 'orcanos_url', side: 'jira', label: 'Orcanos item link', example: 'app.orcanos.com/…' },
];
// A GitHub issue has no custom fields, so nothing on GitHub can hold the Orcanos ID: only the
// Orcanos side gets write-backs, and the link to Orcanos is a comment on the issue.
const GH_WRITEBACKS = [
  { key: 'jira_key', side: 'orc', label: 'GitHub issue reference', example: 'acme/firmware#42' },
  { key: 'jira_url', side: 'orc', label: 'GitHub issue link', example: 'github.com/acme/firmware/issues/42' },
];

/** Every picker list is A→Z by what the user reads (case-insensitive, "2" before "10"). */
const az = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base', numeric: true });
const sortBy = (list, key) => [...(list || [])].sort((a, b) => az(key(a), key(b)));

const newId = () => Math.random().toString(36).slice(2, 10);

// The preview shows the body GitHub actually receives — the engine's Jira-shaped `project` /
// `issuetype` are dropped by github_client.create_issue (a real type, not "issue", becomes `type`).
const ghPreview = (fields) => {
  const { project, issuetype, ...body } = fields || {};
  const t = issuetype && issuetype.id;
  return t && t !== 'issue' && !('type' in body) ? { ...body, type: t } : body;
};

const EDITABLE = [
  'name', 'orcanos_user', 'orc_project_id', 'orc_project_name', 'orc_version_id', 'orc_version_label',
  'orc_type_code', 'orc_type_label', 'orc_filter_id', 'orc_filter_name', 'poll_minutes', 'jira_site',
  'jira_email', 'jira_project_key', 'jira_project_name', 'jira_issue_type_id', 'jira_issue_type_name',
  'conditions', 'fields', 'create_in_jira', 'create_in_orcanos', 'update_in_jira', 'update_in_orcanos',
  'remote_link', 'sync_images', 'sync_attachments', 'conflict_rule',
];

const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** The dry run's images and attachments: what would be copied, or what is left behind because
 *  its switch is off. Sizes are unknown for an Orcanos image until it is downloaded. */
function MediaPreview({ media }) {
  const kinds = [['images', 'Images', 'Sync images', media.images_on],
                 ['attachments', 'Attachments', 'Sync attachments', media.attachments_on]];
  return (
    <div className="intg-media-preview">
      {kinds.map(([key, label, setting, on]) => {
        const list = media[key] || [];
        const to = list[0]?.to;
        return (
          <div key={key} className={`intg-note ${list.length && on ? 'intg-note--ok' : 'intg-note--info'}`}>
            <b>{label}</b>{' · '}
            {!list.length ? 'nothing to copy'
              : on ? `${list.length} would be copied to ${to}`
                : `${list.length} found, not copied: “${setting}” is off`}
            {list.length > 0 && (
              <ul className="intg-media-list">
                {list.map((f, i) => <li key={`${f.name}-${i}`}>{f.name}{f.size ? <span className="intg-hint"> · {kb(f.size)}</span> : null}</li>)}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** A password input with a Show / Hide toggle. */
function SecretInput({ show, onToggle, ...props }) {
  return (
    <span className="intg-secret">
      <input {...props} type={show ? 'text' : 'password'} autoComplete="new-password" />
      <button type="button" className="intg-secret-eye" onClick={onToggle}
              aria-label={show ? 'Hide' : 'Show'}>{show ? 'Hide' : 'Show'}</button>
    </span>
  );
}

function Seg({ options, value, onChange, label }) {
  return (
    <div className="intg-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={String(value) === String(o.value)}
                onClick={() => onChange(o.value)} title={o.title}>{o.label}</button>
      ))}
    </div>
  );
}

/** One value box: a searchable picker when the side has a pick list, else free text.
 *  The value being edited is kept as an option, so a value outside the list still shows. */
function ValueBox({ id, options, value, onChange, placeholder, ariaLabel }) {
  if (!options.length) {
    return <input id={id} value={value || ''} placeholder={placeholder} aria-label={ariaLabel}
                  onChange={(e) => onChange(e.target.value)} />;
  }
  const opts = [...(placeholder ? [{ value: '', label: placeholder }] : []),
    ...(value && !options.includes(value) ? [{ value, label: value }] : []),
    ...sortBy(options, (o) => o).map((o) => ({ value: o, label: o }))];
  return <SearchSelect ariaLabel={ariaLabel} placeholder={placeholder || 'Search…'} value={value || ''}
                       options={opts} onChange={(v) => onChange(String(v))} />;
}

function ValueMap({ row, orcOptions, jiraOptions, onChange, P = 'Jira' }) {
  const pairs = row.value_map || [];
  const toJira = row.direction !== 'to_orcanos';
  const toOrc = row.direction !== 'to_jira';
  const set = (i, k, v) => onChange({ value_map: pairs.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const suggest = () => {
    const have = new Set(pairs.map((p) => (p.orc || '').toLowerCase()));
    const add = [];
    orcOptions.forEach((o) => {
      if (have.has(o.toLowerCase())) return;
      const match = jiraOptions.find((j) => j.toLowerCase() === o.toLowerCase());
      add.push({ orc: o, jira: match || '' });
    });
    if (!orcOptions.length) {
      jiraOptions.forEach((j) => { if (!pairs.some((p) => (p.jira || '').toLowerCase() === j.toLowerCase())) add.push({ orc: '', jira: j }); });
    }
    onChange({ value_map: [...pairs, ...add] });
  };
  return (
    <div className="intg-vm">
      <div className="intg-vm-head"><span>Orcanos value</span><span /><span>{P} value</span><span /></div>
      {pairs.map((p, i) => (
        <div className="intg-vm-row" key={i}>
          <ValueBox id={`vm-o-${row.id}-${i}`} options={orcOptions} value={p.orc} onChange={(v) => set(i, 'orc', v)} ariaLabel="Orcanos value" />
          <span className="intg-arrow">{DIRECTION_LABEL[row.direction]}</span>
          <ValueBox id={`vm-j-${row.id}-${i}`} options={jiraOptions} value={p.jira} onChange={(v) => set(i, 'jira', v)} ariaLabel={`${P} value`} />
          <button type="button" className="intg-btn intg-btn--small intg-btn--ghost" aria-label="Remove pair"
                  onClick={() => onChange({ value_map: pairs.filter((_, j) => j !== i) })}>✕</button>
        </div>
      ))}
      <div className="intg-row-actions">
        <button type="button" className="intg-btn intg-btn--small" onClick={() => onChange({ value_map: [...pairs, { orc: '', jira: '' }] })}>+ Add pair</button>
        {(orcOptions.length > 0 || jiraOptions.length > 0) && (
          <button type="button" className="intg-btn intg-btn--small" onClick={suggest}>Fill from the pick lists</button>
        )}
      </div>
      {/* Same sides as the pairs above: Orcanos on the left, the partner on the right — even when one is hidden. */}
      <div className="intg-grid2">
        {toOrc ? (
          <label className="intg-field">
            <span>Anything else, in Orcanos</span>
            <ValueBox id={`vm-do-${row.id}`} options={orcOptions} value={row.default_orc} placeholder="send unchanged"
                      ariaLabel="Anything else, in Orcanos" onChange={(v) => onChange({ default_orc: v })} />
          </label>
        ) : <span />}
        {toJira ? (
          <label className="intg-field">
            <span>Anything else, in {P}</span>
            <ValueBox id={`vm-dj-${row.id}`} options={jiraOptions} value={row.default_jira} placeholder="send unchanged"
                      ariaLabel={`Anything else, in ${P}`} onChange={(v) => onChange({ default_jira: v })} />
          </label>
        ) : <span />}
      </div>
      <p className="intg-hint">An empty value, or one with no pair, gets the default. With no default, it is sent unchanged and the run log shows a warning.</p>
    </div>
  );
}

export default function IntegrationBuilder() {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [server, setServer] = useState(null);
  const [secrets, setSecrets] = useState({ orcanos_password: '', jira_token: '' });
  const [showSecret, setShowSecret] = useState({ orc: false, jira: false });
  // The step lives in the URL (?step=N), so a refresh keeps the user where they were. A copy in
  // sessionStorage covers the trip to Atlassian / GitHub and back, whose return URL drops the query.
  const stepKey = `intg-step-${id}`;
  const [step, setStepRaw] = useState(() => {
    const q = Number(new URLSearchParams(window.location.search).get('step'));
    let n = q;
    if (!n) { try { n = Number(sessionStorage.getItem(stepKey)); } catch (e) { n = 0; } }
    return Math.min(6, Math.max(1, n || 1));
  });
  const setStep = useCallback((n) => {
    setStepRaw(n);
    try { sessionStorage.setItem(stepKey, String(n)); } catch (e) { /* storage blocked */ }
    const q = new URLSearchParams(window.location.search);
    q.set('step', String(n));
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${q}`);
  }, [stepKey]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [tests, setTests] = useState({});
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState(null);
  const [jiraProjects, setJiraProjects] = useState(null);
  const [issueTypes, setIssueTypes] = useState(null);
  const [jiraFields, setJiraFields] = useState(null);
  const [orcFields, setOrcFields] = useState(null);
  const [metaError, setMetaError] = useState('');
  const [openVm, setOpenVm] = useState(null);
  const [dry, setDry] = useState({ orcanos_id: '', jira_key: '', result: null, busy: false });
  const [made, setMade] = useState({ busy: false, result: null });
  const [hook, setHook] = useState(null);

  const flash = (t) => { setToast(t); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    try {
      const d = await intgApi.get(id);
      setServer(d);
      setForm(Object.fromEntries(EDITABLE.map((k) => [k, d[k]])));
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { orcanosProjects().then(setProjects).catch(() => setProjects([])); }, []);

  // Back from Atlassian: ?jira=connected&jira_site=… or ?jira_error=… — say so once, then tidy the URL.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('jira') === 'connected') {
      const many = Number(q.get('jira_sites') || 0) > 1;
      setTests((t) => ({ ...t, jira: { ok: true, text: `Connected to ${q.get('jira_site') || 'Jira'} with Atlassian.${many ? ' You approved several sites; this one was picked. Type the site in the box below and connect again to choose another.' : ''}` } }));
    } else if (q.get('jira_error')) {
      setTests((t) => ({ ...t, jira: { ok: false, text: q.get('jira_error') } }));
    }
    if (q.get('github') === 'connected') {
      const scopes = q.get('github_scopes');
      setTests((t) => ({ ...t, jira: { ok: !scopes, text: scopes
        ? `Connected to GitHub, but only with ${scopes}. Connect again and allow repository access.`
        : 'Connected to GitHub.' } }));
    } else if (q.get('github_error')) {
      setTests((t) => ({ ...t, jira: { ok: false, text: q.get('github_error') } }));
    }
    if (q.get('jira') || q.get('jira_error') || q.get('github') || q.get('github_error')) {
      window.history.replaceState(window.history.state, '', `${window.location.pathname}?step=${step}`);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [jiraMode, setJiraMode] = useState(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async (quiet = false) => {
    setSaving(true);
    try {
      const body = { ...form };
      if (secrets.orcanos_password) body.orcanos_password = secrets.orcanos_password;
      if (secrets.jira_token) body.jira_token = secrets.jira_token;
      const d = await intgApi.save(id, body);
      setServer(d);
      setForm(Object.fromEntries(EDITABLE.map((k) => [k, d[k]])));
      setSecrets({ orcanos_password: '', jira_token: '' });
      setError('');
      if (!quiet) flash('Saved');
      return d;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------ metadata, loaded when its step needs it

  const projectChoices = useMemo(() => projects.map((p) => ({
    value: `${p.project_id}|${p.version_id}`,
    label: `${p.project_name} (${p.version_label})${p.is_solution ? ' (Solution)' : ''}`,
    strong: !!p.is_solution,
    p,
  })).sort((a, b) => az(a.label, b.label)), [projects]);
  const projectEntry = projectChoices.find((c) => c.value === `${form?.orc_project_id}|${form?.orc_version_id}`)?.p;
  const typeChoices = sortBy(projectEntry?.item_types, (t) => t.label || t.code);

  useEffect(() => {
    if (!form?.orc_project_id || !form?.orc_type_code) { setFilters(null); return; }
    let live = true;
    orcanosFilters(form.orc_project_id, form.orc_type_code, form.orc_type_label)
      .then((d) => { if (live) setFilters(d); })
      .catch(() => { if (live) setFilters({ available: false, filters: [] }); });
    return () => { live = false; };
  }, [form?.orc_project_id, form?.orc_type_code, form?.orc_type_label]);

  const loadJiraProjects = useCallback(async () => {
    try {
      setJiraProjects((await intgApi.jiraProjects(id)).projects || []);
      setMetaError('');
    } catch (e) { setMetaError(e.message); }
  }, [id]);

  useEffect(() => {
    if (step === 3 && server?.has_jira_token && jiraProjects === null) loadJiraProjects();
  }, [step, server?.has_jira_token, jiraProjects, loadJiraProjects]);

  useEffect(() => {
    if (!form?.jira_project_key || !server?.has_jira_token || (step !== 3 && step !== 4)) return;
    let live = true;
    intgApi.jiraIssueTypes(id, form.jira_project_key)
      .then((d) => { if (live) setIssueTypes(d.issue_types || []); })
      .catch((e) => { if (live) setMetaError(e.message); });
    return () => { live = false; };
  }, [id, form?.jira_project_key, server?.has_jira_token, step]);

  useEffect(() => {
    if (!form?.jira_project_key || !form?.jira_issue_type_id || !server?.has_jira_token || (step !== 3 && step !== 4)) return;
    let live = true;
    intgApi.jiraFields(id, form.jira_project_key, form.jira_issue_type_id)
      .then((d) => { if (live) setJiraFields(d.fields || []); })
      .catch((e) => { if (live) setMetaError(e.message); });
    return () => { live = false; };
  }, [id, form?.jira_project_key, form?.jira_issue_type_id, server?.has_jira_token, step]);

  useEffect(() => {
    if (step !== 4 || !server?.orc_type_code) return;
    let live = true;
    intgApi.orcanosFields(id)
      .then((d) => { if (live) setOrcFields(d.fields || []); })
      .catch((e) => { if (live) setMetaError(e.message); });
    return () => { live = false; };
  }, [id, step, server?.orc_type_code, server?.orc_version_id]);

  if (!form) {
    return <div className="intg-page">{error ? <div className="intg-note intg-note--bad">{error}</div> : <p className="intg-sub">Loading…</p>}</div>;
  }
  if (server && !server.can_edit) {
    return <div className="intg-page"><div className="intg-note intg-note--bad">Only an Orcanos administrator can change an integration.</div></div>;
  }

  const P = partnerOf(server);
  const isGh = P === 'GitHub';
  const STEPS = stepsFor(P);

  const go = async (n) => {
    const saved = await save(true);
    if (saved) setStep(Math.min(6, Math.max(1, n)));
  };

  // ------------------------------------------------ step 1

  const testOrcanos = async () => {
    setTests((t) => ({ ...t, orc: { busy: true } }));
    try {
      const d = await intgApi.testOrcanos(id, { orcanos_user: form.orcanos_user || '', orcanos_password: secrets.orcanos_password });
      const rights = d.permission
        ? (d.can_add && d.can_edit ? `This user can add and edit ${form.orc_type_label || 'the chosen type'}.`
          : `This user cannot ${!d.can_add ? 'add' : 'edit'} ${form.orc_type_label || 'the chosen type'} in this project, so those writes will fail.`)
        : '';
      setTests((t) => ({ ...t, orc: { ok: true, text: `Signed in as ${d.display_name}. ${rights}` } }));
    } catch (e) { setTests((t) => ({ ...t, orc: { ok: false, text: e.message } })); }
  };

  const oauthAvailable = !!server?.jira_oauth_available;
  const connectedOAuth = server?.jira_auth === 'oauth' && server?.has_jira_token;
  const mode = jiraMode || (connectedOAuth || (oauthAvailable && !server?.has_jira_token) ? 'oauth' : 'token');

  const connectAtlassian = async () => {
    const saved = await save(true);
    if (!saved) return;
    try {
      const start = isGh ? intgApi.githubOauthStart : intgApi.oauthStart;
      const d = await start(id, { origin: window.location.origin, return_path: window.location.pathname });
      window.location.assign(d.url);
    } catch (e) { setTests((t) => ({ ...t, jira: { ok: false, text: e.message } })); }
  };

  const disconnectAtlassian = async () => {
    try {
      await (isGh ? intgApi.githubDisconnect : intgApi.oauthDisconnect)(id);
      setTests((t) => ({ ...t, jira: null }));
      load();
    } catch (e) { setError(e.message); }
  };

  const testJira = async () => {
    setTests((t) => ({ ...t, jira: { busy: true } }));
    try {
      const d = await intgApi.testJira(id, { jira_site: form.jira_site || '', jira_email: form.jira_email || '', jira_token: secrets.jira_token });
      setTests((t) => ({ ...t, jira: { ok: true, text: `Connected to ${d.site.replace('https://', '')} as ${d.display_name || d.email}.` } }));
      if (isGh) load();
    } catch (e) { setTests((t) => ({ ...t, jira: { ok: false, text: e.message } })); }
  };

  const step1 = (
    <div className="intg-stack">
      <div><h2>Connect</h2><p className="intg-sub">The integration runs on its own, with nobody signed in, so it keeps its own sign-in for each side.</p></div>
      <label className="intg-field"><span>Name</span>
        <input id="i-name" value={form.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder={isGh ? 'Defects ⇄ GitHub issues' : 'Defects ⇄ Jira bugs'} />
      </label>
      {isGh ? (
      <div className="intg-box">
        <h3><span className="intg-side intg-side--gh">GitHub</span></h3>
        <div className="intg-radio">
          <label>
            <input type="radio" name="gauth" id="ga-sso" checked={mode === 'oauth'} disabled={!oauthAvailable && !connectedOAuth}
                   onChange={() => setJiraMode('oauth')} />
            <span><b>Connect with GitHub</b>
              <small>{oauthAvailable
                ? 'Sign in to GitHub (through your organisation\'s SSO when it has one) and approve access. No token to copy.'
                : 'Needs your GitHub app first: enter it on the Integrations page under GitHub app. Or use a personal access token.'}</small></span>
          </label>
          <label>
            <input type="radio" name="gauth" id="ga-tok" checked={mode === 'token'} onChange={() => setJiraMode('token')} />
            <span><b>Personal access token</b><small>A fine-grained or classic token from GitHub.</small></span>
          </label>
        </div>
        {mode === 'oauth' ? (
          <div className="intg-conn">
            {connectedOAuth ? (
              <>
                <span className="intg-ok">✓ Connected to GitHub{server.jira_account_name ? ` as ${server.jira_account_name}` : ''}</span>
                <button type="button" className="intg-btn intg-btn--small" onClick={connectAtlassian} disabled={!oauthAvailable}>Reconnect</button>
                <button type="button" className="intg-btn intg-btn--small" onClick={testJira}>Test connection</button>
                {!server.enabled && <button type="button" className="intg-btn intg-btn--small intg-btn--ghost" onClick={disconnectAtlassian}>Disconnect</button>}
              </>
            ) : (
              <button type="button" className="intg-btn intg-btn--gh" onClick={connectAtlassian} disabled={!oauthAvailable}>Connect with GitHub</button>
            )}
            {tests.jira && !tests.jira.busy && <span className={tests.jira.ok ? 'intg-ok' : 'intg-bad'}>{tests.jira.ok ? '✓ ' : ''}{tests.jira.text}</span>}
          </div>
        ) : (<>
          <label className="intg-field"><span>Personal access token</span>
            <SecretInput id="i-gtoken" show={showSecret.jira} onToggle={() => setShowSecret((x) => ({ ...x, jira: !x.jira }))} value={secrets.jira_token}
                   onChange={(e) => setSecrets((s) => ({ ...s, jira_token: e.target.value }))}
                   placeholder={server?.has_jira_token ? 'Saved. Type a new one to replace it' : 'Paste the token'} />
          </label>
          <p className="intg-hint">
            Create it at GitHub → Settings → Developer settings → Personal access tokens. Fine-grained: give it the
            repository with <b>Issues: Read and write</b> (and <b>Webhooks: Read and write</b> if it may add the
            webhook). Classic: the <span className="intg-mono">repo</span> scope. Use a dedicated GitHub account if you can:
            GitHub shows its changes under that name.
          </p>
          <div className="intg-conn">
            <button type="button" className="intg-btn" onClick={testJira} disabled={tests.jira?.busy}>{tests.jira?.busy ? 'Testing…' : 'Test connection'}</button>
            {tests.jira && !tests.jira.busy && <span className={tests.jira.ok ? 'intg-ok' : 'intg-bad'}>{tests.jira.ok ? '✓ ' : ''}{tests.jira.text}</span>}
          </div>
        </>)}
      </div>
      ) : (
      <div className="intg-box">
        <h3><span className="intg-side intg-side--jira">Jira Cloud</span></h3>
        <div className="intg-radio">
          <label>
            <input type="radio" name="jauth" id="ja-sso" checked={mode === 'oauth'} disabled={!oauthAvailable && !connectedOAuth}
                   onChange={() => setJiraMode('oauth')} />
            <span><b>Connect with Atlassian</b>
              <small>{oauthAvailable
                ? 'Sign in through your company\'s Atlassian login (SSO) and approve access. No token to copy, and Jira notifications are set up for you.'
                : 'Needs your Atlassian app first: enter it on the Integrations page under Atlassian app. Or use an API token.'}</small></span>
          </label>
          <label>
            <input type="radio" name="jauth" id="ja-tok" checked={mode === 'token'} onChange={() => setJiraMode('token')} />
            <span><b>API token</b><small>An Atlassian account email and an API token.</small></span>
          </label>
        </div>
        {mode === 'oauth' ? (
          <div className="intg-conn">
            {connectedOAuth ? (
              <>
                <span className="intg-ok">✓ Connected to {(server.jira_site || '').replace('https://', '')}{server.jira_account_name ? ` as ${server.jira_account_name}` : ''}</span>
                <button type="button" className="intg-btn intg-btn--small" onClick={connectAtlassian} disabled={!oauthAvailable}>Reconnect</button>
                <button type="button" className="intg-btn intg-btn--small" onClick={testJira}>Test connection</button>
                {!server.enabled && <button type="button" className="intg-btn intg-btn--small intg-btn--ghost" onClick={disconnectAtlassian}>Disconnect</button>}
              </>
            ) : (
              <button type="button" className="intg-btn intg-btn--jira" onClick={connectAtlassian} disabled={!oauthAvailable}>Connect with Atlassian</button>
            )}
            {tests.jira && !tests.jira.busy && <span className={tests.jira.ok ? 'intg-ok' : 'intg-bad'}>{tests.jira.ok ? '✓ ' : ''}{tests.jira.text}</span>}
          </div>
        ) : (<>
        <div className="intg-grid2">
          <label className="intg-field"><span>Jira site</span>
            <input id="i-jsite" value={form.jira_site || ''} onChange={(e) => set({ jira_site: e.target.value })} placeholder="yourcompany.atlassian.net" />
          </label>
          <label className="intg-field"><span>Atlassian account email</span>
            <input id="i-jemail" type="email" value={form.jira_email || ''} onChange={(e) => set({ jira_email: e.target.value })} placeholder="integration@yourcompany.com" />
          </label>
          <label className="intg-field"><span>API token</span>
            <SecretInput id="i-jtoken" show={showSecret.jira} onToggle={() => setShowSecret((x) => ({ ...x, jira: !x.jira }))} value={secrets.jira_token}
                   onChange={(e) => setSecrets((s) => ({ ...s, jira_token: e.target.value }))}
                   placeholder={server?.has_jira_token ? 'Saved. Type a new one to replace it' : 'Paste the API token'} />
          </label>
        </div>
        <p className="intg-hint">
          Create the token at id.atlassian.com → Security → API tokens, signed in as the account above.
          Use a dedicated account if you can: Jira shows its changes under that name.
        </p>
        <div className="intg-conn">
          <button type="button" className="intg-btn" onClick={testJira} disabled={tests.jira?.busy}>{tests.jira?.busy ? 'Testing…' : 'Test connection'}</button>
          {tests.jira && !tests.jira.busy && <span className={tests.jira.ok ? 'intg-ok' : 'intg-bad'}>{tests.jira.ok ? '✓ ' : ''}{tests.jira.text}</span>}
        </div>
        </>)}
      </div>
      )}
      <div className="intg-box">
        <h3><span className="intg-side intg-side--orc">Orcanos</span></h3>
        <div className="intg-grid2">
          <label className="intg-field"><span>Orcanos user</span>
            <input id="i-ouser" autoComplete="off" value={form.orcanos_user || ''} onChange={(e) => set({ orcanos_user: e.target.value })} />
          </label>
          <label className="intg-field"><span>Password</span>
            <SecretInput id="i-opwd" show={showSecret.orc} onToggle={() => setShowSecret((x) => ({ ...x, orc: !x.orc }))} value={secrets.orcanos_password}
                   onChange={(e) => setSecrets((s) => ({ ...s, orcanos_password: e.target.value }))}
                   placeholder={server?.has_orcanos_password ? 'Saved. Type a new one to replace it' : ''} />
          </label>
        </div>
        <p className="intg-hint">Signs in at <span className="intg-mono">{server?.orcanos_url || 'the Orcanos address you signed in with'}</span>.</p>
        <div className="intg-conn">
          <button type="button" className="intg-btn" onClick={testOrcanos} disabled={tests.orc?.busy}>{tests.orc?.busy ? 'Testing…' : 'Test sign-in'}</button>
          {tests.orc && !tests.orc.busy && <span className={tests.orc.ok ? 'intg-ok' : 'intg-bad'}>{tests.orc.ok ? '✓ ' : ''}{tests.orc.text}</span>}
        </div>
        <p className="intg-hint">Stored encrypted. Items this integration creates or changes in Orcanos are written as this user, with this user's permissions.</p>
      </div>
    </div>
  );

  // ------------------------------------------------ step 2

  // A different work item type (or the project, which clears it) has different fields, so the
  // field map is emptied — after the user agrees. Nothing to warn about when no field is mapped yet.
  const keepFields = (changed) => {
    if (!changed || !(form.fields || []).length) return true;
    return window.confirm('Changing the work item type resets the Fields step: every mapped field is removed, '
      + 'because the new type does not necessarily have the same fields. Continue?');
  };

  const pickProject = (value) => {
    const c = projectChoices.find((x) => x.value === value);
    if (!c) return;
    const changed = `${c.p.project_id}|${c.p.version_id}` !== `${form.orc_project_id}|${form.orc_version_id}` && !!form.orc_type_code;
    if (!keepFields(changed)) return;
    if (changed) { set({ fields: [] }); setOrcFields(null); }
    set({ orc_project_id: String(c.p.project_id), orc_project_name: c.p.project_name,
          orc_version_id: String(c.p.version_id), orc_version_label: c.p.version_label,
          orc_type_code: '', orc_type_label: '', orc_filter_id: '', orc_filter_name: '' });
  };
  const pickType = (code) => {
    const t = typeChoices.find((x) => x.code === code);
    const changed = !!form.orc_type_code && code !== form.orc_type_code;
    if (!keepFields(changed)) return;
    if (changed) { set({ fields: [] }); setOrcFields(null); }
    set({ orc_type_code: code, orc_type_label: t?.label || code, orc_filter_id: '', orc_filter_name: '' });
  };

  // Project, type and filter are required for the integration to run, but the builder never
  // blocks on them — a missing one is marked in red here and on the step list instead.
  const orcMissing = [
    !form.orc_project_id && 'project',
    !form.orc_type_code && 'work item type',
    !form.orc_filter_id && 'filter',
  ].filter(Boolean);
  const Req = () => <b className="intg-req" aria-hidden="true">*</b>;
  const missingHint = (what) => <span className="intg-missing" role="status">Required: no {what} chosen yet</span>;

  const step2 = (
    <div className="intg-stack">
      <div><h2>Orcanos side</h2><p className="intg-sub">The filter decides which items take part. Any work item type works.</p></div>
      <div className="intg-grid2">
        <label className={`intg-field${form.orc_project_id ? '' : ' intg-field--missing'}`}><span>Project and version<Req /></span>
          <SearchSelect ariaLabel="Project and version" placeholder="Search projects…" options={projectChoices}
                        value={`${form.orc_project_id || ''}|${form.orc_version_id || ''}`} onChange={pickProject} />
          {!form.orc_project_id && missingHint('project')}
        </label>
        <label className={`intg-field${form.orc_type_code ? '' : ' intg-field--missing'}`}><span>Work item type<Req /></span>
          <SearchSelect ariaLabel="Work item type" placeholder={projectEntry ? 'Search work item types…' : 'Choose a project first'}
                        disabled={!projectEntry} value={form.orc_type_code || ''} onChange={pickType}
                        options={typeChoices.map((t) => ({ value: t.code, label: t.label || t.code }))} />
          {!form.orc_type_code && missingHint('work item type')}
        </label>
        <label className={`intg-field${form.orc_filter_id ? '' : ' intg-field--missing'}`}><span>Saved filter<Req /></span>
          {filters && filters.available && filters.filters.length > 0 ? (
            <SearchSelect ariaLabel="Saved filter" placeholder="Search filters…" value={form.orc_filter_id || ''}
                          options={sortBy(filters.filters, (f) => f.name).map((f) => ({ value: String(f.id), label: f.name }))}
                          onChange={(v) => {
                            const f = filters.filters.find((x) => String(x.id) === String(v));
                            set({ orc_filter_id: String(v), orc_filter_name: f?.name || '' });
                          }} />
          ) : (
            <input id="i-ofilter" inputMode="numeric" placeholder={form.orc_type_code ? 'Filter ID' : 'Choose a type first'}
                   value={form.orc_filter_id || ''} disabled={!form.orc_type_code}
                   onChange={(e) => set({ orc_filter_id: e.target.value.replace(/\D/g, ''), orc_filter_name: '' })} />
          )}
          {!form.orc_filter_id && missingHint('filter')}
          <OpenFilterLink baseUrl={server?.orcanos_url} versionId={form.orc_version_id}
                          itemTypeCode={form.orc_type_code} filterId={form.orc_filter_id || ''} />
        </label>
      </div>
      {orcMissing.length > 0 && (
        <div className="intg-note intg-note--bad" role="status">
          <b>Missing:</b> {orcMissing.join(', ')}. You can carry on and come back, but the integration cannot run until {orcMissing.length > 1 ? 'they are' : 'it is'} chosen.
        </div>
      )}
      <div className="intg-note intg-note--info">
        The filter must show the <b>Updated Date</b> and <b>Created Date</b> columns. Without them the
        integration cannot tell which items changed, and it will say so when it is switched on.
      </div>
      <div className="intg-field"><span>Check Orcanos for changes</span>
        <Seg label="Check every" options={POLL_CHOICES} value={form.poll_minutes} onChange={(v) => set({ poll_minutes: v })} />
        <span className="intg-hint">Orcanos cannot send a notification, so the integration checks. {P} changes arrive straight away when {P}'s webhook is set up (step 6), and with each check otherwise.</span>
      </div>
    </div>
  );

  // ------------------------------------------------ step 3

  const jf = sortBy(jiraFields, (f) => f.name);
  const jfById = Object.fromEntries(jf.map((f) => [f.id, f]));
  const conditions = form.conditions || [];
  const setCond = (i, patch) => set({ conditions: conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  const step3 = (
    <div className="intg-stack">
      <div><h2>{P} side</h2><p className="intg-sub">Which {P} issues take part.</p></div>
      {!server?.has_jira_token && <div className="intg-note intg-note--warn">Save the {P} connection in step 1 first.</div>}
      {metaError && <div className="intg-note intg-note--bad">{metaError}</div>}
      <div className="intg-grid2">
        <label className="intg-field"><span>{isGh ? 'Repository' : 'Jira project'}</span>
          <SearchSelect ariaLabel={isGh ? 'Repository' : 'Jira project'}
                        placeholder={jiraProjects === null ? 'Loading…' : isGh ? 'Search repositories…' : 'Search Jira projects…'}
                        value={form.jira_project_key || ''}
                        options={[
                          ...(form.jira_project_key && !(jiraProjects || []).some((p) => p.key === form.jira_project_key)
                            ? [{ value: form.jira_project_key, label: `${form.jira_project_key} ${form.jira_project_name || ''}` }] : []),
                          ...sortBy(jiraProjects, (p) => p.name).map((p) => ({ value: p.key,
                            label: isGh ? `${p.name}${p.private ? ' (private)' : ''}` : `${p.name} (${p.key})` })),
                        ]}
                        onChange={(v) => {
                          const p = (jiraProjects || []).find((x) => x.key === v);
                          set({ jira_project_key: v, jira_project_name: p?.name || '', jira_issue_type_id: '', jira_issue_type_name: '' });
                          setIssueTypes(null); setJiraFields(null);
                        }} />
        </label>
        <label className="intg-field"><span>Issue type</span>
          <SearchSelect ariaLabel="Issue type" placeholder={form.jira_project_key ? 'Search issue types…' : `Choose a ${isGh ? 'repository' : 'Jira project'} first`}
                        disabled={!form.jira_project_key} value={form.jira_issue_type_id || ''}
                        options={[
                          ...(form.jira_issue_type_id && !(issueTypes || []).some((t) => String(t.id) === String(form.jira_issue_type_id))
                            ? [{ value: form.jira_issue_type_id, label: form.jira_issue_type_name || form.jira_issue_type_id }] : []),
                          ...sortBy((issueTypes || []).filter((t) => !t.subtask), (t) => t.name).map((t) => ({ value: String(t.id), label: t.name })),
                        ]}
                        onChange={(v) => {
                          const t = (issueTypes || []).find((x) => String(x.id) === String(v));
                          set({ jira_issue_type_id: String(v), jira_issue_type_name: t?.name || '' });
                          setJiraFields(null);
                        }} />
        </label>
      </div>
      <div className="intg-field"><span>Only issues where (all must match)</span>
        <div className="intg-conds">
          {conditions.map((c, i) => (
            <div className="intg-cond" key={i}>
              <SearchSelect ariaLabel={`${P} field`} placeholder="Search fields…" value={c.field || ''}
                            options={[...(c.field && !jfById[c.field] ? [{ value: c.field, label: c.label || c.field }] : []),
                                      ...jf.map((f) => ({ value: f.id, label: f.name }))]}
                            onChange={(v) => {
                              const f = jfById[v];
                              setCond(i, { field: String(v), label: f?.name || '', schema: f?.schema || {} });
                            }} />
              <SearchSelect ariaLabel="Condition" placeholder="Condition" value={c.op || 'is'}
                            options={COND_OPS} onChange={(v) => setCond(i, { op: String(v) })} />
              {c.op === 'empty' || c.op === 'not_empty'
                ? <input id={`c-v-${i}`} aria-label="Value" value="" disabled placeholder="No value needed" />
                : <ValueBox id={`c-v-${i}`} ariaLabel="Value" placeholder="" value={c.value}
                            options={jfById[c.field]?.allowed || []} onChange={(v) => setCond(i, { value: v })} />}
              <button type="button" className="intg-btn intg-btn--small intg-btn--ghost" aria-label="Remove condition"
                      onClick={() => set({ conditions: conditions.filter((_, j) => j !== i) })}>✕</button>
            </div>
          ))}
          <div>
            <button type="button" className="intg-btn intg-btn--small" disabled={!jf.length}
                    onClick={() => set({ conditions: [...conditions, { field: '', op: 'is', value: '' }] })}>+ Add condition</button>
          </div>
        </div>
        <span className="intg-hint">
          {isGh
            ? 'Leave empty to take every issue in the repository (pull requests never take part). A common condition: Labels contains orcanos. '
            : 'Leave empty to take every issue of this type in the project. '}
          Conditions decide which new {P} issues become Orcanos items; issues already linked always stay in step.
        </span>
      </div>
    </div>
  );

  // ------------------------------------------------ step 4

  const rows = form.fields || [];
  const of = sortBy(orcFields, (f) => f.title || f.name);
  const ofByName = Object.fromEntries(of.map((f) => [f.name, f]));
  const setRow = (rid, patch) => set({ fields: rows.map((r) => (r.id === rid ? { ...r, ...patch } : r)) });
  const fieldRows = rows.filter((r) => (r.kind || 'field') !== 'writeback');
  const wb = (key) => rows.find((r) => r.kind === 'writeback' && r.writeback === key);
  const writebacks = isGh ? GH_WRITEBACKS : WRITEBACKS;
  const setWb = (key, value) => {
    const def = writebacks.find((w) => w.key === key);
    const others = rows.filter((r) => !(r.kind === 'writeback' && r.writeback === key));
    if (!value) { set({ fields: others }); return; }
    const row = def.side === 'orc'
      ? { id: `wb-${key}`, kind: 'writeback', writeback: key, orc_field: value, orc_title: ofByName[value]?.title || value }
      : { id: `wb-${key}`, kind: 'writeback', writeback: key, jira_field: value, jira_label: jfById[value]?.name || value };
    set({ fields: [...others, row] });
  };
  const mappedJira = new Set(rows.filter((r) => r.jira_field && r.direction !== 'to_orcanos').map((r) => r.jira_field));
  const missingRequired = form.create_in_jira === false ? []
    : jf.filter((f) => f.required && !mappedJira.has(f.id) && !['summary', 'reporter', 'title'].includes(f.id));
  // The same for a new Orcanos item: its Add form's mandatory fields (the name takes the Jira summary,
  // and Orcanos sets the first status). Edit-only fields are not on the Add form, so they can't be asked for.
  const mappedOrc = new Set(rows.filter((r) => r.orc_field && r.direction !== 'to_jira').map((r) => r.orc_field));
  const missingOrc = form.create_in_orcanos === false ? []
    : of.filter((f) => f.mandatory && !f.edit_only && !mappedOrc.has(f.name)
      && !['obj_name', 'object_name', 'obj_status'].includes(String(f.name).toLowerCase()));
  // A written-back ID or link needs a field that can hold it: text or URL, plus a NUMBER field for the
  // Orcanos item ID (field_value converts it). Same names and `*` as the field map; the rest are left out.
  const jiraHolds = (f, key) => ['string', 'any'].includes(f.schema?.type) || f.schema?.custom?.includes('url')
    || (key === 'orcanos_id' && f.schema?.type === 'number');
  const orcHolds = (f) => !['list', 'multiSelect', 'checkBox', 'date'].includes(f.type);

  const addRow = (kind) => set({ fields: [...rows, kind === 'constant'
    ? { id: newId(), kind: 'constant', direction: 'to_jira', jira_field: '', constant: '' }
    : { id: newId(), kind: 'field', direction: 'both', orc_field: '', jira_field: '', value_map: [] }] });

  const step4 = (
    <div className="intg-stack">
      <div><h2>Field map</h2><p className="intg-sub">Choose the direction for each field: → sends Orcanos to {P}, ← sends {P} to Orcanos, ↔ keeps both in step.</p></div>
      {metaError && <div className="intg-note intg-note--bad">{metaError}</div>}
      {(orcFields === null || jiraFields === null) && !metaError && <p className="intg-sub">Reading the fields of both sides…</p>}
      <div className="intg-table-wrap">
        <table className="intg-table intg-map">
          <thead><tr><th>Orcanos field</th><th>Direction</th><th>{P} field</th><th>Values</th><th /></tr></thead>
          <tbody>
            {fieldRows.map((r) => {
              const o = ofByName[r.orc_field];
              const j = jfById[r.jira_field];
              const hasLists = (o?.options?.length || 0) > 0 || (j?.allowed?.length || 0) > 0;
              return (
                <React.Fragment key={r.id}>
                  <tr>
                    <td>
                      {r.kind === 'constant' ? (
                        <input id={`m-c-${r.id}`} aria-label="Fixed value" placeholder="Fixed value" value={r.constant || ''}
                               onChange={(e) => setRow(r.id, { constant: e.target.value })} />
                      ) : (
                        <SearchSelect ariaLabel="Orcanos field" placeholder="Choose…" value={r.orc_field || ''}
                                      options={[...(r.orc_field && !o ? [{ value: r.orc_field, label: r.orc_title || r.orc_field }] : []),
                                                ...of.map((f) => ({ value: f.name, label: `${f.title}${f.mandatory && !f.edit_only ? ' *' : ''}` }))]}
                                      onChange={(v) => {
                                        const f = ofByName[v];
                                        setRow(r.id, { orc_field: String(v), orc_title: f?.title || '', orc_type: f?.type || '' });
                                      }} />
                      )}
                    </td>
                    <td>
                      {r.kind === 'constant' ? <span className="intg-mono">→</span> : (
                        <Seg label="Direction" value={r.direction} onChange={(v) => setRow(r.id, { direction: v })}
                             options={[{ value: 'to_jira', label: '→', title: `Orcanos to ${P}` },
                                       { value: 'to_orcanos', label: '←', title: `${P} to Orcanos` },
                                       { value: 'both', label: '↔', title: 'Both ways' }]} />
                      )}
                    </td>
                    <td>
                      <SearchSelect ariaLabel={`${P} field`} placeholder="Choose…" value={r.jira_field || ''}
                                    options={[...(r.jira_field && !j ? [{ value: r.jira_field, label: r.jira_label || r.jira_field }] : []),
                                              ...jf.map((f) => ({ value: f.id, label: `${f.name}${f.required ? ' *' : ''}` }))]}
                                    onChange={(v) => {
                                      const f = jfById[v];
                                      setRow(r.id, { jira_field: String(v), jira_label: f?.name || '', jira_schema: f?.schema || {} });
                                    }} />
                    </td>
                    <td>
                      {r.kind === 'constant' ? <span className="intg-tag">Fixed value</span>
                        : (hasLists || (r.value_map || []).length > 0) ? (
                          <button type="button" className="intg-btn intg-btn--small" aria-expanded={openVm === r.id}
                                  onClick={() => setOpenVm(openVm === r.id ? null : r.id)}>
                            {(r.value_map || []).length ? `${r.value_map.length} mapped` : 'Map values'}
                            {r.default_jira ? ` · default ${r.default_jira}` : (r.value_map || []).length ? ' · no default' : ''}
                          </button>
                        ) : <span className="intg-tag">{j && (j.schema?.system === 'description' || j.schema?.system === 'body' || j.schema?.custom?.includes('textarea')) ? 'Rich text, converted' : 'As is'}</span>}
                    </td>
                    <td>
                      <button type="button" className="intg-btn intg-btn--small intg-btn--ghost" aria-label="Remove row"
                              onClick={() => set({ fields: rows.filter((x) => x.id !== r.id) })}>✕</button>
                    </td>
                  </tr>
                  {openVm === r.id && (
                    <tr className="intg-vm-tr"><td colSpan={5}>
                      <ValueMap row={r} orcOptions={o?.options || []} jiraOptions={j?.allowed || []} P={P}
                                onChange={(patch) => setRow(r.id, patch)} />
                    </td></tr>
                  )}
                </React.Fragment>
              );
            })}
            {fieldRows.length === 0 && <tr><td colSpan={5} className="intg-muted">No fields yet. Start with the title: Orcanos Title ↔ {isGh ? 'GitHub Title' : 'Jira Summary'}.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="intg-row-actions">
        <button type="button" className="intg-btn intg-btn--small" onClick={() => addRow('field')}>+ Add field</button>
        <button type="button" className="intg-btn intg-btn--small" onClick={() => addRow('constant')}>+ Add fixed value</button>
      </div>
      {missingRequired.length > 0 && (
        <div className="intg-note intg-note--bad" role="alert">
          <b>Mandatory in {P}:</b> {missingRequired.map((f) => f.name).join(', ')} on a new {form.jira_issue_type_name || 'issue'}.
          Map {missingRequired.length > 1 ? 'them' : 'it'} (→ or ↔) or give {missingRequired.length > 1 ? 'them' : 'it'} a fixed value, or creating {P} issues will fail.
        </div>
      )}
      {missingOrc.length > 0 && (
        <div className="intg-note intg-note--bad" role="alert">
          <b>Mandatory in Orcanos:</b> {missingOrc.map((f) => f.title || f.name).join(', ')} on a new {form.orc_type_label || 'item'}.
          Map {missingOrc.length > 1 ? 'them' : 'it'} from a {P} field (← or ↔), or creating Orcanos items will fail.
        </div>
      )}

      <div className="intg-box">
        <h3>IDs and links written back</h3>
        <p className="intg-hint">{isGh
          ? 'When an item is created on one side, Orcanos gets the GitHub issue reference and link. A GitHub issue has no custom fields, so the link back to Orcanos is a comment on the issue.'
          : 'When an item is created on one side, the other side gets its ID and link. Choose where each one goes, or leave it out.'}</p>
        <div className="intg-grid2">
          {writebacks.map((w) => (
            <label className="intg-field" key={w.key}>
              <span>{w.label} → {w.side === 'orc' ? 'Orcanos field' : `${P} field`}</span>
              <SearchSelect ariaLabel={w.label} placeholder="Don't write it"
                            value={(w.side === 'orc' ? wb(w.key)?.orc_field : wb(w.key)?.jira_field) || ''}
                            options={[{ value: '', label: "Don't write it" },
                                      // Only fields that can hold the value are offered (a searchable list has no disabled rows).
                                      ...(w.side === 'orc'
                                        ? of.filter(orcHolds).map((f) => ({ value: f.name, label: f.title }))
                                        : jf.filter((f) => jiraHolds(f, w.key)).map((f) => ({ value: f.id, label: `${f.name}${f.required ? ' *' : ''}` })))]}
                            onChange={(v) => setWb(w.key, String(v))} />
              <span className="intg-hint intg-mono">e.g. {w.example}</span>
            </label>
          ))}
        </div>
        <label className="intg-check">
          <input id="i-rlink" type="checkbox" checked={!!form.remote_link} onChange={(e) => set({ remote_link: e.target.checked })} />
          {isGh ? 'Post a comment with the Orcanos link on each GitHub issue' : 'Also add an "Orcanos" web link on each Jira issue'}
        </label>
      </div>
    </div>
  );

  // ------------------------------------------------ step 5

  const step5 = (
    <div className="intg-stack">
      <div><h2>Rules</h2></div>
      <div className="intg-field"><span>Events — what is carried across</span></div>
      <div className="intg-box">
        <div className="intg-hint"><b>Orcanos → {P}</b></div>
        <label className="intg-switch">
          <span><b>Add</b><br /><span className="intg-hint">A new item in the Orcanos filter becomes a {P} {isGh ? 'issue' : form.jira_issue_type_name || 'issue'}.</span></span>
          <input id="r-cj" type="checkbox" checked={!!form.create_in_jira} onChange={(e) => set({ create_in_jira: e.target.checked })} />
        </label>
        <label className="intg-switch">
          <span><b>Update</b><br /><span className="intg-hint">An edit to a linked Orcanos item updates its {P} issue (fields mapped → or ↔).</span></span>
          <input id="r-uj" type="checkbox" checked={form.update_in_jira !== false} onChange={(e) => set({ update_in_jira: e.target.checked })} />
        </label>
        <div className="intg-hint"><b>{P} → Orcanos</b></div>
        <label className="intg-switch">
          <span><b>Add</b><br /><span className="intg-hint">A new matching {P} issue becomes an Orcanos {form.orc_type_label || 'item'} in {form.orc_project_name || 'the project'}{form.orc_version_label ? ` v${form.orc_version_label}` : ''}.</span></span>
          <input id="r-co" type="checkbox" checked={!!form.create_in_orcanos} onChange={(e) => set({ create_in_orcanos: e.target.checked })} />
        </label>
        <label className="intg-switch">
          <span><b>Update</b><br /><span className="intg-hint">An edit to a linked {P} issue updates its Orcanos item (fields mapped ← or ↔).</span></span>
          <input id="r-uo" type="checkbox" checked={form.update_in_orcanos !== false} onChange={(e) => set({ update_in_orcanos: e.target.checked })} />
        </label>
      </div>
        <>
          <div className="intg-field"><span>{isGh
            ? 'Images and attachments — GitHub → Orcanos only (GitHub has no way for an integration to upload a file)'
            : 'Images and attachments — both ways, with the events above'}</span></div>
          <div className="intg-box">
            <label className="intg-switch">
              <span><b>Sync images</b><br /><span className="intg-hint">{isGh
                ? 'Pictures uploaded to the GitHub issue body are copied into the Orcanos text, so they show in Orcanos.'
                : 'Pictures inside a mapped text field (Description) are copied with the text, so they show on the other side.'}</span></span>
              <input id="r-img" type="checkbox" checked={form.sync_images !== false} onChange={(e) => set({ sync_images: e.target.checked })} />
            </label>
            <label className="intg-switch">
              <span><b>Sync attachments</b><br /><span className="intg-hint">{isGh
                ? 'Files uploaded to the GitHub issue body are copied to the Orcanos item’s Attachments tab. A file whose name is already there is not copied again.'
                : `Files attached to the ${P} issue or the Orcanos item are copied to the other side. A file whose name is already there is not copied again.`}</span></span>
              <input id="r-att" type="checkbox" checked={form.sync_attachments !== false} onChange={(e) => set({ sync_attachments: e.target.checked })} />
            </label>
          </div>
        </>
      <div className="intg-field"><span>If both sides changed the same field since the last sync</span>
        <div className="intg-radio">
          {[
            ['orcanos', 'Orcanos wins', `Orcanos is the system of record. The ${P} change is overwritten and logged.`],
            ['jira', `${P} wins`, 'The Orcanos value is overwritten and logged.'],
            ['latest', 'Latest change wins', 'Whichever side changed most recently.'],
          ].map(([v, t, d]) => (
            <label key={v}>
              <input type="radio" name="conflict" id={`cf-${v}`} checked={form.conflict_rule === v} onChange={() => set({ conflict_rule: v })} />
              <span><b>{t}</b><small>{d}</small></span>
            </label>
          ))}
        </div>
      </div>
      <div className="intg-note intg-note--info">
        Deleting an item never deletes its partner. The link is marked “source deleted” in the run log.<br />
        Switching on syncs new changes only. Items that already exist join when they next change.
      </div>
    </div>
  );

  // ------------------------------------------------ step 6

  const runDry = async () => {
    setDry((d) => ({ ...d, busy: true, result: null }));
    setMade({ busy: false, result: null });
    // The dry run reads the SAVED setup — save first, or edits made since run on the old map.
    if (!(await save(true))) { setDry((d) => ({ ...d, busy: false })); return; }
    try {
      const r = await intgApi.dryRun(id, { orcanos_id: dry.orcanos_id, jira_key: dry.orcanos_id ? '' : dry.jira_key });
      setDry((d) => ({ ...d, busy: false, result: r }));
    } catch (e) { setDry((d) => ({ ...d, busy: false, result: { error: e.message } })); }
  };

  // "Create it now": the same item, for real — the proof a dry run cannot give.
  const createNow = async () => {
    const what = dry.orcanos_id ? `Orcanos item ${dry.orcanos_id}` : `${P} issue ${dry.jira_key}`;
    if (!window.confirm(`This writes for real: ${what} gets its partner created (or synced) now. Continue?`)) return;
    setMade({ busy: true, result: null });
    try {
      const r = await intgApi.tryOne(id, { orcanos_id: dry.orcanos_id, jira_key: dry.orcanos_id ? '' : dry.jira_key });
      setMade({ busy: false, result: r });
    } catch (e) { setMade({ busy: false, result: { outcome: 'error', message: e.message } }); }
  };

  const toggle = async (on) => {
    const saved = await save(true);
    if (!saved) return;
    try {
      const d = await intgApi.enable(id, on);
      setServer(d);
      setHook(on ? { state: d.webhook, message: d.webhook_message } : null);
      flash(on ? 'Switched on. The first check starts now.' : 'Switched off');
    } catch (e) { setError(e.message); }
  };

  const retryHook = async () => {
    try {
      const d = await intgApi.registerWebhook(id);
      setHook({ state: d.webhook, message: d.webhook_message });
    } catch (e) { setError(e.message); }
  };

  const result = dry.result;
  const step6 = (
    <div className="intg-stack">
      <div><h2>Test and enable</h2><p className="intg-sub">Try one real item. <b>Dry run</b> only shows what would be sent. <b>Create it now</b> then does it for real.</p></div>
      <div className="intg-conn">
        <label className="intg-field intg-grow"><span>Orcanos item ID</span>
          <input id="d-oid" value={dry.orcanos_id} placeholder="e.g. 18233" onChange={(e) => setDry((d) => ({ ...d, orcanos_id: e.target.value }))} />
        </label>
        <span className="intg-muted">or</span>
        <label className="intg-field intg-grow"><span>{isGh ? 'GitHub issue number' : 'Jira issue key'}</span>
          <input id="d-jkey" value={dry.jira_key} placeholder={isGh ? 'e.g. 42' : 'e.g. PUMP-1042'} onChange={(e) => setDry((d) => ({ ...d, jira_key: e.target.value }))} />
        </label>
        <button type="button" className="intg-btn" onClick={runDry} disabled={dry.busy || (!dry.orcanos_id && !dry.jira_key)}>{dry.busy ? 'Reading…' : 'Dry run'}</button>
      </div>
      {result?.error && <div className="intg-note intg-note--bad">{result.error}</div>}
      {result && !result.error && (
        <div className="intg-note intg-note--info">
          Preview only: nothing was created. Check the values below, then click <b>{result.already_linked ? 'Sync it now' : 'Create it now'}</b> to do it for real.
        </div>
      )}
      {result && !result.error && (
        <div className="intg-field">
          <span>{result.already_linked
            ? `Already linked (${result.link?.orcanos_id} ⇄ ${result.link?.jira_key}). A new partner would not be created; the fields below show what a sync would send.`
            : result.would_create === 'jira'
              ? `Would create in ${P} (${form.jira_project_key}${isGh ? '' : ` · ${form.jira_issue_type_name}`})`
              : `Would create in Orcanos (${form.orc_type_label})`}</span>
          {result.conditions_pass === false && <div className="intg-note intg-note--warn">This issue does not pass the {P} conditions ({result.conditions_reason}), so it would be left out.</div>}
          <pre className="intg-pre">{JSON.stringify(result.would_create === 'jira'
            ? { ...(isGh ? ghPreview(result.fields) : result.fields), ...(result.status ? { status: result.status } : {}) }
            : { name: result.name, description: result.description, ...result.fields }, null, 2)}</pre>
          {result.media && <MediaPreview media={result.media} />}
          {(result.missing_required || []).length > 0 && <div className="intg-note intg-note--bad" role="alert">{result.would_create === 'jira' ? P : 'Orcanos'} requires {result.missing_required.join(', ')}, which {result.missing_required.length > 1 ? 'are' : 'is'} not mapped.</div>}
          {(result.warnings || []).map((w) => <div key={w} className="intg-note intg-note--warn">{w}</div>)}
          {result.conditions_pass !== false && (
            <div className="intg-conn">
              <button type="button" className="intg-btn intg-btn--primary" onClick={createNow} disabled={made.busy}>
                {made.busy ? 'Working…' : result.already_linked ? 'Sync it now' : 'Create it now'}
              </button>
            </div>
          )}
        </div>
      )}
      {made.result && (() => {
        const m = made.result;
        const L = m.link || {};
        const good = m.outcome === 'created' || m.outcome === 'updated' || m.outcome === 'nothing';
        return (
          <div className={`intg-note ${good ? 'intg-note--ok' : m.outcome === 'skipped' ? 'intg-note--warn' : 'intg-note--bad'}`}>
            <div><b>{m.outcome === 'created' ? '✓ Created' : m.outcome === 'updated' ? '✓ Synced' : m.outcome === 'nothing' ? '✓ Already in step' : m.outcome === 'skipped' ? 'Skipped' : 'Failed'}</b> · {m.message}</div>
            {(L.jira_key || L.orcanos_id) && (
              <div className="intg-made-links">
                {L.jira_key && <span>{P}: {L.jira_url ? <a href={L.jira_url} target="_blank" rel="noreferrer">{L.jira_key} ↗</a> : L.jira_key}</span>}
                {L.orcanos_id && <span>Orcanos: {L.orcanos_url ? <a href={L.orcanos_url} target="_blank" rel="noreferrer">{L.orcanos_id} ↗</a> : L.orcanos_id}</span>}
              </div>
            )}
            {(m.warnings || []).map((w) => <div key={w}>⚠ {w}</div>)}
          </div>
        );
      })()}

      <div className="intg-box">
        <h3>Switch on</h3>
        <p className="intg-hint">Switching on reads what the filter holds now and syncs only what changes from then on.</p>
        <div className="intg-conn">
          {server?.enabled
            ? <button type="button" className="intg-btn" onClick={() => toggle(false)}>Switch off</button>
            : <button type="button" className="intg-btn intg-btn--primary" onClick={() => toggle(true)}>Switch on</button>}
          {server?.enabled && <span className="intg-ok">✓ On. <Link to={`/integrations/${id}/log`}>Open the run log</Link></span>}
        </div>
        {hook?.state === 'registered' && <div className="intg-note intg-note--info">✓ {hook.message}</div>}
        {(hook?.state === 'manual' || (server?.enabled && !server?.jira_webhook_id)) && (
          <div className="intg-note intg-note--warn">
            <div>{hook?.message || `${P} is not set up to notify Orcanos yet, so ${P} changes arrive with each check.`}</div>
            {server?.webhook_url_manual && isGh && (
              <div className="intg-hookhelp">
                <div>For instant updates, an administrator of <b>{form.jira_project_key}</b> adds a webhook at
                  <b> Settings → Webhooks → Add webhook</b>:</div>
                <dl>
                  <dt>Payload URL</dt><dd className="intg-mono intg-break">{server.webhook_url_manual}</dd>
                  <dt>Content type</dt><dd className="intg-mono">application/json</dd>
                  <dt>Events</dt><dd>Let me select individual events → <b>Issues</b> only</dd>
                </dl>
                <span className="intg-hint">The URL contains this integration's secret. Share it only with the repository administrator. Leave the Secret box empty.</span>
                <div><button type="button" className="intg-btn intg-btn--small" onClick={retryHook}>Try adding it automatically again</button></div>
              </div>
            )}
            {server?.webhook_url_manual && !isGh && (
              <div className="intg-hookhelp">
                <div>For instant updates, a Jira administrator adds a webhook at <b>Jira settings → System → WebHooks</b>:</div>
                <dl>
                  <dt>URL</dt><dd className="intg-mono intg-break">{server.webhook_url_manual}</dd>
                  <dt>Events</dt><dd>{(server.webhook_events || []).join(', ')}</dd>
                  <dt>JQL</dt><dd className="intg-mono">{server.webhook_jql}</dd>
                </dl>
                <span className="intg-hint">The URL contains this integration's secret. Share it only with the Jira administrator.</span>
                <div><button type="button" className="intg-btn intg-btn--small" onClick={retryHook}>Try adding it automatically again</button></div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const panels = { 1: step1, 2: step2, 3: step3, 4: step4, 5: step5, 6: step6 };

  return (
    <div className="intg-page">
      <div className="intg-crumbs"><Link to={`/integrations?type=${server?.connector || 'jira'}`}>{P} integrations</Link> / {form.name || 'Integration'}</div>
      <div className="intg-head">
        <div><h1>{form.name || 'New integration'}</h1>
          <p className="intg-sub">An Orcanos filter on one side, a {isGh ? 'GitHub repository' : 'Jira project'} on the other.</p></div>
      </div>
      {error && <div className="intg-note intg-note--bad" role="alert">{error}</div>}
      <div className="intg-builder">
        <nav className="intg-steps" aria-label="Setup steps">
          {STEPS.map((s) => (
            <button key={s.n} type="button" className={`intg-step${s.n < step ? ' intg-step--done' : ''}`}
                    aria-current={s.n === step ? 'step' : undefined} onClick={() => go(s.n)}>
              <span className="intg-step-n">{s.n}</span>
              <span><b>{s.title}{s.n === 2 && orcMissing.length > 0 && <span className="intg-req" title={`Missing: ${orcMissing.join(', ')}`}> *</span>}</b>
                <small>{s.n === 2 && orcMissing.length > 0 ? <span className="intg-missing">Missing {orcMissing.join(', ')}</span> : s.sub}</small></span>
            </button>
          ))}
        </nav>
        <div className="intg-panel">
          {panels[step]}
          <div className="intg-foot">
            <button type="button" className="intg-btn" onClick={() => go(step - 1)} disabled={step === 1 || saving}>Back</button>
            <div className="intg-row-actions">
              <button type="button" className="intg-btn" onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              {step < 6 && <button type="button" className="intg-btn intg-btn--primary" onClick={() => go(step + 1)} disabled={saving}>Next</button>}
            </div>
          </div>
        </div>
      </div>
      {toast && <div className="intg-toast" role="status">{toast}</div>}
    </div>
  );
}
